import {
  collection,
  CollectionReference,
  doc,
  DocumentReference,
  DocumentSnapshot,
  onSnapshot,
  setDoc,
} from "firebase/firestore";
import { set } from "lodash";
import { useAnalytics } from "Providers/AnalyticsProvider";
import React, { createContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useFirestore, useFirestoreCollectionData } from "reactfire";
import { PasswordDialog } from "../../Components/PasswordDialog";
import { mergeListOverrides } from "./overrides";
import { Artist, ArtistOverride, ShareLists } from "./types";
import { listDefaults } from "./listDefaults";
import { EmailCapture } from "Components/EmailCapture";
import { useLocalStorage } from "Hooks";

interface ListAuthOptions {
  password?: string;
  invalidPassword?: boolean;
  isDialogOpen?: boolean;
}

interface ListContextProps {
  listId: string;
  list: ShareLists;
  artists: Artist[];
  loading: boolean;
  save: (path?: string) => void;
  saveLoading: boolean;
  loadAnimation?: boolean;
  setLoadAnimation: (v: boolean) => void;
  setList: (property: keyof ShareLists, value: any) => void;
}

export const ListContext = createContext({} as ListContextProps);

export const ListProvider = ({
  children,
  clientSide,
}: {
  children: React.ReactNode;
  clientSide?: boolean;
}) => {
  const [list, setListDetails] = useState<ShareLists>();
  const [listAuth, setListAuth] = useState<ListAuthOptions>({});
  const [listDocLoading, setListDocLoading] = useState<boolean>(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const { track } = useAnalytics();
  const [loadAnimation, setLoadAnimation] = useState(true);
  const [emailCapture, setEmailCapture] = useState(false);
  const db = useFirestore();
  const { listId } = useParams();
  const navigate = useNavigate();
  const [userEmail, setUserEmail] = useLocalStorage(`userEmail:${listId}`, "");

  const updateUserEmail = (email: string) => {
    setUserEmail(email);
    setEmailCapture(false);
  };

  const handleError = (error: Error) => {
    console.error(error);
    navigate("/");
  };

  useEffect(() => {
    if (!listId) return;
    setListDocLoading(true);

    track({
      type: "view:list",
      ...(userEmail && { email: userEmail }),
    });

    try {
      const unsubscribe = onSnapshot(
        doc(db, "ame_sharelists", listId) as DocumentReference<ShareLists>,
        (snap) => handleSnapshot(snap),
        (error) => handleError(error),
      );

      return () => unsubscribe();
    } catch (error) {
      console.error(error);
    }
  }, [listId, userEmail]);

  const handleSnapshot = (snap: DocumentSnapshot<ShareLists>) => {
    const localPass = localStorage.getItem(listId);

    if (snap.exists()) {
      if (snap.get("hasPassword") && clientSide) {
        if (localPass) {
          setListAuth((prev) => ({ ...prev, password: localPass }));
        } else {
          setListAuth({ isDialogOpen: true });
        }
      }
      if (
        snap.get("emailCapture.enabled") === true &&
        !userEmail &&
        clientSide
      ) {
        setEmailCapture(true);
      }
      const snapshot = snap.data();
      const shareListData = { ...listDefaults, ...snapshot };
      setListDetails(shareListData);
    } else {
      navigate("/");
    }

    setListDocLoading(false);
  };

  const { data: artistData, status: artistStatus } = useFirestoreCollectionData(
    collection(
      db,
      `ame_sharelists/${listId}/artists`,
    ) as CollectionReference<Artist>,
  );

  const { data: artistOverrides, status: overrideStatus } =
    useFirestoreCollectionData(
      collection(
        db,
        `ame_sharelists/${listId}/artist_overrides`,
      ) as CollectionReference<ArtistOverride>,
      {
        idField: "id",
      },
    );

  const save = async (path?: string) => {
    setSaveLoading(true);
    try {
      await setDoc(doc(db, "ame_sharelists", listId), list, { merge: true });

      toast.success("List has been saved.");

      if (path) {
        navigate(path);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setSaveLoading(false);
    }
  };

  const setList = (property: keyof ShareLists, value: any) => {
    const listDetails = { ...list };
    const updatedList = set(listDetails, property, value);
    setListDetails(updatedList);
  };

  const loading =
    listDocLoading ||
    artistStatus === "loading" ||
    overrideStatus === "loading";

  const artists = useMemo(() => {
    return mergeListOverrides(artistData, artistOverrides, list?.order);
  }, [artistData, artistOverrides, list?.order]);

  return (
    <ListContext.Provider
      value={{
        listId,
        artists,
        loading,
        list, // Current local state of list.
        setList, // Method for updating list details.
        save, // Save current state of list to firestore.
        saveLoading,
        loadAnimation,
        setLoadAnimation,
      }}
    >
      {!listAuth.isDialogOpen ? (
        <>
          {children}
          {emailCapture && (
            <EmailCapture
              onSubmit={updateUserEmail}
              emailCapture={list?.emailCapture}
            />
          )}
        </>
      ) : (
        <PasswordDialog
          onSubmit={(pass) =>
            setListAuth({ ...listAuth, invalidPassword: false, password: pass })
          }
          isInvalid={listAuth.invalidPassword}
          loading={loading}
        />
      )}
    </ListContext.Provider>
  );
};
